/**
 * Internationalization (i18n) Module
 * Supports English (en) and Japanese (ja)
 */

const translations = {
    en: {
        // Header
        'app.title': 'Stock Chart Viewer',
        'search.placeholder': 'Enter symbol (e.g., AAPL)',
        'mode.stock': 'Stock',
        'mode.crypto': 'Crypto',

        // Periods
        'period.1D': '1D',
        'period.1W': '1W',
        'period.1M': '1M',
        'period.3M': '3M',
        'period.1Y': '1Y',

        // Indicators
        'indicator.sma20': 'SMA20',
        'indicator.sma50': 'SMA50',
        'indicator.bb': 'BB',
        'indicator.rsi': 'RSI',
        'indicator.macd': 'MACD',
        'indicator.ichimoku': 'Ichimoku',
        'indicator.stoch': 'Stoch',

        // Panels
        'watchlist.title': 'Watchlist',
        'portfolio.title': 'Portfolio',
        'portfolio.total': 'Total Value',
        'portfolio.pl': 'Total P/L',
        'alerts.title': 'Price Alerts',
        'news.title': 'Market News',
        'backtest.title': 'Strategy Backtester',

        // Trading
        'trade.buy': 'Buy',
        'trade.sell': 'Sell',
        'wallet.label': 'Wallet',

        // Actions
        'action.export': 'Export CSV',
        'action.snapshot': 'Snapshot',
        'action.settings': 'Settings',

        // Sidebar
        'sidebar.chart': 'Chart',
        'sidebar.portfolio': 'Portfolio',
        'sidebar.alerts': 'Alerts',
        'sidebar.theme': 'Theme',
        'sidebar.language': 'Language',
        'sidebar.install': 'Install App',

        // Messages
        'msg.noData': 'No data available',
        'msg.loading': 'Loading...',
        'msg.loading': 'Loading...',
        'msg.error': 'An error occurred',

        // AI & Cache
        'ai.title': 'AI Assistant',
        'ai.btnAnalyze': 'Analyze',
        'action.analyze': 'Analyze',
        'cache.title': 'Cache Manager',
        'cache.apiUsage': 'API Usage',
        'cache.mode': 'Cache Only Mode',
        'cache.storage': 'Storage:',
        'cache.dataList': 'Saved Data',

        // Stats & Welcome
        'stat.open': 'Open',
        'stat.high': 'High',
        'stat.low': 'Low',
        'stat.vol': 'Vol',
        'welcome.title': 'Stock Chart Viewer',
        'welcome.desc': 'Enter a symbol regarding the ticker symbol above',
        'footer.provider': 'Data Provider:',
        'footer.limit': 'Limit:',
    },
    ja: {
        // Header
        'app.title': '株価チャートビューア',
        'search.placeholder': 'シンボル入力 (例: AAPL)',
        'mode.stock': '株式',
        'mode.crypto': '仮想通貨',

        // Periods
        'period.1D': '1日',
        'period.1W': '1週',
        'period.1M': '1月',
        'period.3M': '3月',
        'period.1Y': '1年',

        // Indicators
        'indicator.sma20': 'SMA20',
        'indicator.sma50': 'SMA50',
        'indicator.bb': 'BB',
        'indicator.rsi': 'RSI',
        'indicator.macd': 'MACD',
        'indicator.ichimoku': '一目',
        'indicator.stoch': 'ストキャス',

        // Panels
        'watchlist.title': 'ウォッチリスト',
        'portfolio.title': 'ポートフォリオ',
        'portfolio.total': '総資産額',
        'portfolio.pl': '損益合計',
        'alerts.title': '価格アラート',
        'news.title': 'マーケットニュース',
        'backtest.title': 'バックテスト',

        // Trading
        'trade.buy': '買い',
        'trade.sell': '売り',
        'wallet.label': '残高',

        // Actions
        'action.export': 'CSV出力',
        'action.snapshot': 'スナップショット',
        'action.settings': '設定',

        // Sidebar
        'sidebar.chart': 'チャート',
        'sidebar.portfolio': 'ポートフォリオ',
        'sidebar.alerts': 'アラート',
        'sidebar.theme': 'テーマ',
        'sidebar.language': '言語',
        'sidebar.install': 'アプリ追加',

        // Messages
        'msg.noData': 'データがありません',
        'msg.loading': '読み込み中...',
        'msg.loading': '読み込み中...',
        'msg.error': 'エラーが発生しました',

        // AI & Cache
        'ai.title': 'AI アシスタント',
        'ai.btnAnalyze': '分析',
        'action.analyze': '分析',
        'cache.title': 'キャッシュ管理',
        'cache.apiUsage': 'API使用状況',
        'cache.mode': 'キャッシュ専用モード',
        'cache.storage': '保存先:',
        'cache.dataList': '保存済みデータ',

        // Stats & Welcome
        'stat.open': '始値',
        'stat.high': '高値',
        'stat.low': '安値',
        'stat.vol': '出来高',
        'welcome.title': '株価チャートを表示',
        'welcome.desc': '上部の検索ボックスにティッカーシンボルを入力してください',
        'footer.provider': 'データ提供:',
        'footer.limit': '制限:',
    }
};

let currentLanguage = localStorage.getItem('language') || 'en';

/**
 * Get translation for a key
 */
export function t(key) {
    return translations[currentLanguage]?.[key] || translations['en']?.[key] || key;
}

/**
 * Set the current language and persist
 */
export function setLanguage(lang) {
    if (translations[lang]) {
        currentLanguage = lang;
        localStorage.setItem('language', lang);
        applyTranslations();
        return true;
    }
    return false;
}

/**
 * Get the current language code
 */
export function getLanguage() {
    return currentLanguage;
}

/**
 * Apply translations to all elements with data-i18n attribute
 */
export function applyTranslations() {
    document.querySelectorAll('[data-i18n]').forEach(el => {
        const key = el.dataset.i18n;
        if (el.placeholder !== undefined && key.includes('placeholder')) {
            el.placeholder = t(key);
        } else {
            el.textContent = t(key);
        }
    });

    // Also dispatch event for dynamic components
    document.dispatchEvent(new CustomEvent('language-changed', { detail: { lang: currentLanguage } }));
}

/**
 * Toggle between EN and JA
 */
export function toggleLanguage() {
    const newLang = currentLanguage === 'en' ? 'ja' : 'en';
    setLanguage(newLang);
    return newLang;
}
